import { Injectable, inject } from '@angular/core';
import {
    Firestore,
    doc,
    getDoc,
    setDoc,
    Timestamp
} from '@angular/fire/firestore';
import {
    TipoMovimiento,
    TiposMovimientoConfig,
    TIPOS_MOVIMIENTO_DEFAULT,
    CuentaBancaria,
    CuentasBancariasConfig,
    CUENTA_DEFAULT
} from '../models/financial-config.model';

/**
 * Servicio para gestionar configuraciones financieras en Iglesia-MMM
 * - Tipos de movimiento: Solo lectura desde Settings/tiposMovimiento
 * - Cuentas bancarias: CRUD en iglesias/{churchId}/configuraciones/cuentasBancarias
 */
@Injectable({
    providedIn: 'root'
})
export class BankAccountService {
    private firestore = inject(Firestore);

    // ==================== TIPOS DE MOVIMIENTO (Solo Lectura) ====================

    /**
     * Carga tipos de movimiento desde Settings/tiposMovimiento
     * Si no existen, devuelve valores por defecto (Ingreso y Egreso)
     */
    async getTiposMovimiento(): Promise<TipoMovimiento[]> {
        try {
            const docRef = doc(this.firestore, 'Settings/tiposMovimiento');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data() as TiposMovimientoConfig;
                return data.data?.tipos || TIPOS_MOVIMIENTO_DEFAULT;
            }

            return TIPOS_MOVIMIENTO_DEFAULT;
        } catch (error) {
            console.error('Error cargando tipos de movimiento:', error);
            return TIPOS_MOVIMIENTO_DEFAULT;
        }
    }

    /**
     * Obtiene solo los tipos activos
     */
    async getTiposMovimientoActivos(): Promise<TipoMovimiento[]> {
        const tipos = await this.getTiposMovimiento();
        return tipos.filter(t => t.activo);
    }

    // ==================== CUENTAS BANCARIAS (CRUD) ====================

    /**
     * Carga cuentas bancarias de la iglesia
     */
    async getCuentasBancarias(churchId: string): Promise<CuentaBancaria[]> {
        try {
            const docRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/cuentasBancarias`);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data() as CuentasBancariasConfig;
                return data.data?.cuentas || [CUENTA_DEFAULT];
            }

            return [CUENTA_DEFAULT];
        } catch (error) {
            console.error('Error cargando cuentas bancarias:', error);
            return [CUENTA_DEFAULT];
        }
    }

    /**
     * Guarda cuentas bancarias de la iglesia
     */
    async saveCuentasBancarias(
        churchId: string,
        cuentas: CuentaBancaria[],
        userId: string
    ): Promise<void> {
        try {
            const docRef = doc(this.firestore, `iglesias/${churchId}/configuraciones/cuentasBancarias`);

            const config: CuentasBancariasConfig = {
                data: {
                    cuentas: cuentas
                },
                lastModified: Timestamp.now(),
                modifiedBy: userId
            };

            await setDoc(docRef, config);
        } catch (error) {
            console.error('Error guardando cuentas bancarias:', error);
            throw error;
        }
    }

    /**
     * Agrega una nueva cuenta bancaria
     */
    async addCuentaBancaria(
        churchId: string,
        nuevaCuenta: CuentaBancaria,
        userId: string
    ): Promise<void> {
        const cuentas = await this.getCuentasBancarias(churchId);

        // Validar que no existe ya
        if (cuentas.find(c => c.id === nuevaCuenta.id)) {
            throw new Error(`La cuenta '${nuevaCuenta.id}' ya existe`);
        }

        // Si es default, quitar default de las demás
        if (nuevaCuenta.esDefault) {
            cuentas.forEach(c => c.esDefault = false);
        }

        cuentas.push(nuevaCuenta);
        await this.saveCuentasBancarias(churchId, cuentas, userId);
    }

    /**
     * Actualiza una cuenta bancaria existente
     */
    async updateCuentaBancaria(
        churchId: string,
        updatedCuenta: CuentaBancaria,
        userId: string
    ): Promise<void> {
        const cuentas = await this.getCuentasBancarias(churchId);
        const index = cuentas.findIndex(c => c.id === updatedCuenta.id);

        if (index === -1) {
            throw new Error(`La cuenta '${updatedCuenta.id}' no existe`);
        }

        // Si se marca como default, quitar default de las demás
        if (updatedCuenta.esDefault) {
            cuentas.forEach((c, i) => {
                if (i !== index) c.esDefault = false;
            });
        }

        cuentas[index] = updatedCuenta;
        await this.saveCuentasBancarias(churchId, cuentas, userId);
    }

    /**
     * Elimina una cuenta bancaria
     */
    async deleteCuentaBancaria(
        churchId: string,
        cuentaId: string,
        userId: string
    ): Promise<void> {
        const cuentas = await this.getCuentasBancarias(churchId);

        // No permitir eliminar si es la única cuenta
        if (cuentas.length === 1) {
            throw new Error('No se puede eliminar la única cuenta disponible');
        }

        const cuenta = cuentas.find(c => c.id === cuentaId);

        // No permitir eliminar la cuenta por defecto "caja-menor"
        if (cuentaId === 'caja-menor') {
            throw new Error('No se puede eliminar la cuenta Caja predeterminada');
        }

        const index = cuentas.findIndex(c => c.id === cuentaId);
        if (index === -1) {
            throw new Error(`La cuenta '${cuentaId}' no existe`);
        }

        // Si era default, marcar otra como default
        const wasDefault = cuenta?.esDefault;
        cuentas.splice(index, 1);

        if (wasDefault && cuentas.length > 0) {
            cuentas[0].esDefault = true;
        }

        await this.saveCuentasBancarias(churchId, cuentas, userId);
    }

    /**
     * Obtiene solo las cuentas activas
     */
    async getCuentasActivas(churchId: string): Promise<CuentaBancaria[]> {
        const cuentas = await this.getCuentasBancarias(churchId);
        return cuentas.filter(c => c.activo);
    }

    /**
     * Obtiene la cuenta por defecto
     */
    async getCuentaDefault(churchId: string): Promise<CuentaBancaria | null> {
        const cuentas = await this.getCuentasBancarias(churchId);
        return cuentas.find(c => c.esDefault) || cuentas[0] || null;
    }

    /**
     * Activa/desactiva una cuenta
     */
    async toggleCuentaBancaria(
        churchId: string,
        cuentaId: string,
        userId: string
    ): Promise<void> {
        const cuentas = await this.getCuentasBancarias(churchId);
        const cuenta = cuentas.find(c => c.id === cuentaId);

        if (!cuenta) {
            throw new Error(`La cuenta '${cuentaId}' no existe`);
        }

        cuenta.activo = !cuenta.activo;
        await this.saveCuentasBancarias(churchId, cuentas, userId);
    }
}
